import { useState, useEffect } from 'react';
import { Tablet } from 'lucide-react';
import { kmFormatSpec, kmEvaluateSets, kmSetTarget, kmSetComplete } from '../../engine';
// (enkeltmatch live-scoring center module)

export interface SingleMatchSingleCourtPadProps {
  /** The single-match activity (tournamentType 'single_match') being played. */
  activity: any;
  /** All players (resolves names/ELO). */
  allPlayers?: any[];
  /** Finalises the result (writes the match + ELO): (activity, { team1, team2, sets, playedAt }). */
  onRecordResult?: any;
  /** Persists the live (in-progress) score continuously: (activity, liveScore). */
  onLiveScore?: any;
  /** Called when a player name is clicked. */
  onPlayerClick?: any;
  /** Accent colour (single_match sky-blue by default). */
  accent?: string;
  /**
   * What THIS court's submission is doing, so «Send resultat» can say it (#216).
   *
   * Reported from the hall: «My eyes focused on the submit button after
   * clicking it. It is human nature.» A banner elsewhere on the screen is not
   * where anyone is looking in the second after they press a button, so the
   * button reports on itself: it goes inert and says «Sender…», then
   * «Registrert», before the court moves on.
   *
   * Resolved by the caller, because the id a result names is the linked
   * BOOKING's and this component only ever sees the activity — the same
   * distinction that made the first submission attempt fail.
   */
  submitPhase?: 'idle' | 'sending' | 'done';
}

// Single-match (enkeltmatch) CENTER MODULE — rendered inside the normal court
// screen frame (BanepadCourtScreen → renderFrame), where a free court shows
// "Spiller nå". It supports LIVE, continuous score entry (+/- per team, game by
// game) so the pad shows the running standing and players can track it. Every
// change is persisted to activity.liveScore via onLiveScore (so it survives a
// reload and shows in the central grid). "Send resultat" finalises (ELO) once
// the match is complete. Set logic comes from the shared engine helpers
// (kmEvaluateSets), honouring the chosen format (super-tiebreak vs full 3rd set).
export function SingleMatchSingleCourtPad({
  activity,
  allPlayers = [],
  onRecordResult,
  onLiveScore,
  onPlayerClick,
  accent = '#0ea5e9',
  submitPhase = 'idle',
}: SingleMatchSingleCourtPadProps) {
  const cfg = activity?.config || {};
  const matchFormat = cfg.matchFormat || 'bo3_super';
  const fmt = kmFormatSpec(matchFormat);
  const needed = Math.ceil(fmt.sets / 2);
  const confirmed = (activity?.registrations || []).filter((r: any) => r.status === 'confirmed');
  const locked = activity?.status === 'completed';

  const playerOf = (pid: string) => allPlayers.find((p) => p.id === pid) || null;
  const nameOf = (pid: string) => playerOf(pid)?.name || '';
  const eloOf = (pid: string) => playerOf(pid)?.elo ?? null;
  const avgElo = (pid1: string, pid2: string) => {
    const a = eloOf(pid1);
    const b = eloOf(pid2);
    if (a == null || b == null) return null;
    return Math.round((a + b) / 2);
  };

  // Two synthetic pairs (team1 = players 1&2, team2 = players 3&4).
  const pairs =
    confirmed.length >= 4
      ? [
          { p1Id: confirmed[0].playerId, p2Id: confirmed[1].playerId },
          { p1Id: confirmed[2].playerId, p2Id: confirmed[3].playerId },
        ]
      : [];

  // Live score state — sets of { t1, t2 } games. Seeded from the persisted
  // liveScore and re-seeded when the match (activity) changes.
  const seed = () => {
    const ls = activity?.liveScore?.sets;
    return Array.isArray(ls) && ls.length
      ? ls.map((s: any) => ({ t1: s.t1 || 0, t2: s.t2 || 0 }))
      : [{ t1: 0, t2: 0 }];
  };
  const [sets, setSets] = useState<any[]>(seed);
  useEffect(() => {
    setSets(seed());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.id]);

  const ev = kmEvaluateSets(sets, fmt);
  const curIdx = sets.length - 1;

  const persist = (arr: any[]) => onLiveScore?.(activity, { sets: arr, updatedAt: Date.now() });

  const addGame = (team: 1 | 2) => {
    if (locked || ev.complete) return;
    const arr = sets.map((s) => ({ ...s }));
    const idx = arr.length - 1;
    arr[idx][team === 1 ? 't1' : 't2']++;
    // Did this set just complete? (target depends on whether it is the decider.)
    const prior = kmEvaluateSets(arr.slice(0, idx), fmt).setWins;
    const isDecider = prior.t1 === needed - 1 && prior.t2 === needed - 1;
    const tgt = kmSetTarget(fmt, isDecider);
    if (kmSetComplete(arr[idx], tgt) && !kmEvaluateSets(arr, fmt).complete) {
      arr.push({ t1: 0, t2: 0 });
    }
    setSets(arr);
    persist(arr);
  };

  const subGame = (team: 1 | 2) => {
    if (locked) return;
    const arr = sets.map((s) => ({ ...s }));
    let idx = arr.length - 1;
    const key = team === 1 ? 't1' : 't2';
    if (arr[idx].t1 === 0 && arr[idx].t2 === 0 && arr.length > 1) {
      arr.pop();
      idx--;
    }
    if (arr[idx][key] > 0) arr[idx][key]--;
    setSets(arr);
    persist(arr);
  };

  const reset = () => {
    if (locked) return;
    const arr = [{ t1: 0, t2: 0 }];
    setSets(arr);
    persist(arr);
  };

  const finalize = () => {
    if (!ev.complete || locked) return;
    const cleanSets = sets
      .filter((s) => s.t1 || s.t2)
      .map((s) => ({ games1: Number(s.t1), games2: Number(s.t2) }));
    onRecordResult?.(activity, {
      team1: { p1Id: pairs[0].p1Id, p2Id: pairs[0].p2Id },
      team2: { p1Id: pairs[1].p1Id, p2Id: pairs[1].p2Id },
      sets: cleanSets,
      playedAt: Date.now(),
    });
  };

  // Waiting for four confirmed players → compact placeholder.
  if (pairs.length < 2) {
    return (
      <div
        className="w-full flex flex-col items-center justify-center text-center"
        style={{ gap: '1cqw' }}
      >
        <Tablet style={{ width: '7cqw', height: '7cqw', opacity: 0.4 }} />
        <div
          className="font-mono uppercase tracking-wider"
          style={{ fontSize: '2cqw', color: accent }}
        >
          Enkeltkamp
        </div>
        <div className="font-mono" style={{ fontSize: '1.8cqw', color: 'rgba(230,251,255,0.5)' }}>
          Venter på fire påmeldte ({confirmed.length}/4).
        </div>
      </div>
    );
  }

  const statusLine = ev.complete
    ? `Ferdig — ${ev.setWins.t1}–${ev.setWins.t2} i sett`
    : ev.curTarget.isTb
      ? `Super-tiebreak til ${ev.curTarget.target}`
      : `Sett ${curIdx + 1} — til ${ev.curTarget.target}`;

  // One team column — two names (clickable) + avg ELO.
  const teamCol = (p: any, align: 'left' | 'right', leads: boolean) => (
    <div className={`flex-1 min-w-0 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      {[
        [p.p1Id, nameOf(p.p1Id)],
        [p.p2Id, nameOf(p.p2Id)],
      ].map(([pid, nm]: any, i: number) => (
        <div
          key={i}
          className="truncate font-display font-bold leading-tight"
          style={{ fontSize: '2.6cqw', color: leads && ev.complete ? '#a3e635' : '#ffffff' }}
        >
          {onPlayerClick ? (
            <button
              type="button"
              onClick={() => onPlayerClick({ id: pid })}
              className="hover:underline"
            >
              {nm}
            </button>
          ) : (
            nm
          )}
        </div>
      ))}
      <div
        className="font-mono"
        style={{ fontSize: '1.5cqw', color: 'rgba(230,251,255,0.5)', marginTop: '0.4cqw' }}
      >
        snitt ELO {avgElo(p.p1Id, p.p2Id) ?? '–'}
      </div>
    </div>
  );

  // +/- stepper pair for one team.
  const stepper = (team: 1 | 2) => (
    <div className="flex items-center" style={{ gap: '1cqw' }}>
      {team === 2 && (
        <button
          onClick={() => addGame(2)}
          className="font-mono font-bold rounded-lg"
          style={{
            width: '7cqw',
            height: '7cqw',
            fontSize: '4cqw',
            background: accent,
            color: '#062c33',
          }}
        >
          +
        </button>
      )}
      <button
        onClick={() => subGame(team)}
        className="font-mono rounded-lg"
        style={{
          width: '5cqw',
          height: '5cqw',
          fontSize: '2.6cqw',
          color: 'rgba(230,251,255,0.7)',
          border: '1px solid rgba(230,251,255,0.2)',
        }}
      >
        −
      </button>
      {team === 1 && (
        <button
          onClick={() => addGame(1)}
          className="font-mono font-bold rounded-lg"
          style={{
            width: '7cqw',
            height: '7cqw',
            fontSize: '4cqw',
            background: accent,
            color: '#062c33',
          }}
        >
          +
        </button>
      )}
    </div>
  );

  const d1 = sets[curIdx]?.t1 ?? 0;
  const d2 = sets[curIdx]?.t2 ?? 0;
  const completedSets = sets.filter((s, i) => i < curIdx || ev.complete);

  return (
    <div className="w-full flex flex-col justify-center" style={{ gap: '1.6cqw' }}>
      {/* Teams + current score in the middle */}
      <div className="flex items-center" style={{ gap: '2cqw' }}>
        {teamCol(pairs[0], 'left', ev.setWins.t1 > ev.setWins.t2)}
        <div className="flex flex-col items-center flex-shrink-0" style={{ minWidth: '20cqw' }}>
          {/* Set wins */}
          <div
            className="font-mono uppercase tracking-wider"
            style={{ fontSize: '1.3cqw', color: 'rgba(230,251,255,0.45)' }}
          >
            Sett {ev.setWins.t1}–{ev.setWins.t2}
          </div>
          {/* Per-set history */}
          {completedSets.length > 0 && (
            <div
              className="flex items-center justify-center font-mono tabular-nums"
              style={{ gap: '1.4cqw', fontSize: '1.8cqw', color: 'rgba(230,251,255,0.6)' }}
            >
              {completedSets.map((s, i) => (
                <span key={i}>
                  {s.t1}-{s.t2}
                </span>
              ))}
            </div>
          )}
          {/* Current game (big) */}
          {!ev.complete && (
            <div
              className="font-mono font-bold tabular-nums leading-none"
              style={{ fontSize: '7cqw', color: '#e6c668', marginTop: '0.4cqw' }}
            >
              {d1}
              <span style={{ opacity: 0.3, margin: '0 1cqw' }}>–</span>
              {d2}
            </div>
          )}
        </div>
        {teamCol(pairs[1], 'right', ev.setWins.t2 > ev.setWins.t1)}
      </div>

      {/* Steppers (hidden once finished / locked) */}
      {!ev.complete && !locked && (
        <div className="flex items-center justify-center" style={{ gap: '6cqw' }}>
          {stepper(1)}
          {stepper(2)}
        </div>
      )}

      {/* Status + actions */}
      <div className="flex items-center justify-between" style={{ gap: '2cqw' }}>
        <span
          className="font-mono uppercase tracking-wider"
          style={{ fontSize: '1.5cqw', color: ev.complete ? '#a3e635' : accent }}
        >
          {statusLine}
        </span>
        <div className="flex items-center" style={{ gap: '1.4cqw' }}>
          {!locked && sets.some((s) => s.t1 || s.t2) && (
            <button
              onClick={reset}
              className="font-mono uppercase tracking-wider rounded-full"
              style={{
                fontSize: '1.5cqw',
                padding: '1cqw 2.2cqw',
                color: 'rgba(230,251,255,0.6)',
                border: '1px solid rgba(230,251,255,0.18)',
              }}
            >
              Nullstill
            </button>
          )}
          {!locked && (
            <button
              onClick={finalize}
              // INERT WHILE IT IS WORKING. The first report from the hall was
              // four presses on a button that looked untouched; the server
              // refused the extra three, but nothing on screen said so.
              disabled={!ev.complete || submitPhase !== 'idle'}
              className="font-mono font-bold uppercase tracking-wider rounded-full"
              style={{
                fontSize: '1.8cqw',
                padding: '1.2cqw 3cqw',
                background:
                  submitPhase === 'done'
                    ? '#16a34a'
                    : submitPhase === 'sending'
                      ? 'rgba(255,255,255,0.14)'
                      : ev.complete
                        ? accent
                        : 'rgba(255,255,255,0.08)',
                color:
                  submitPhase === 'done'
                    ? '#f0fdf4'
                    : submitPhase === 'sending'
                      ? 'rgba(230,251,255,0.75)'
                      : ev.complete
                        ? '#062c33'
                        : 'rgba(230,251,255,0.4)',
                opacity: submitPhase === 'sending' ? 0.9 : 1,
              }}
            >
              {submitPhase === 'done'
                ? 'Registrert ✓'
                : submitPhase === 'sending'
                  ? 'Sender…'
                  : 'Send resultat'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
